import { Link, usePage } from "@inertiajs/react"
// `m` (not `motion`) — this component ships in the shared entry chunk, and
// full `motion` would drag every animation feature into the critical JS on
// every page. Features arrive via the LazyMotion in PersistentLayout (see
// lib/motion-features.ts).
import { AnimatePresence, m, useReducedMotion } from "motion/react"
import { useEffect, useRef, useState } from "react"

import { SiteLogoMark } from "@/components/site-logo-mark"
import { Button } from "@/components/ui/button"
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from "@/components/ui/navigation-menu"
import { springInteractive } from "@/lib/motion"

interface NavLink {
  label: string
  href: string
}

const navItems: NavLink[] = [
  { label: "Services", href: "/services" },
  { label: "Case studies", href: "/case-studies" },
  { label: "About us", href: "/about" },
  { label: "Field notes", href: "/posts" },
]

function ToggleIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
      className="size-6"
    >
      <path
        strokeLinecap="round"
        d={open ? "M17 7 7 17M7 7l10 10" : "M3 8h18M3 16h18"}
      />
    </svg>
  )
}

// `theme="dark"` serves the Working with Robots felt surfaces: the sticky
// light backdrop-blur chrome would wash cream over the dark felt, so dark
// keeps a static header with on-dark ink instead.
export function SiteHeader({ theme = "light" }: { theme?: "light" | "dark" }) {
  const { url } = usePage()
  // Current path without query/hash, so /case-studies?x and /posts/slug both
  // light up their nav item.
  const path = url.split(/[?#]/)[0]
  const isActive = (href: string) =>
    path === href || path.startsWith(`${href}/`)
  const activeHref = navItems.find((item) => isActive(item.href))?.href ?? null
  const [open, setOpen] = useState(false)
  const [hovered, setHovered] = useState<string | null>(null)
  const [scrolled, setScrolled] = useState(false)
  // True while a dark surface (a `.dark` section like the Dark Lime CTA /
  // footer, or a section-scale dark panel opted in via `data-header-dark`)
  // sits under the header band, so the sticky header can flip to on-dark ink.
  const [onDark, setOnDark] = useState(false)
  const headerRef = useRef<HTMLElement>(null)
  const reduceMotion = useReducedMotion()
  const themeDark = theme === "dark"
  // The open mobile panel renders a solid light surface with dark text, so the
  // auto-detected dark treatment must yield to it whenever the menu is expanded.
  const dark = themeDark || (onDark && !open)

  const navText = dark ? "text-stone-300" : "text-gray-80"
  // The current page reads at full ink; hover/focus still tint toward lime.
  const navActive = dark ? "text-white" : "text-gray-100"
  // The pill rests on the current page and glides to a hovered candidate,
  // returning home on mouse leave — one indicator for "you are here" and
  // "you're considering there".
  const pillAt = hovered ?? activeHref
  const navHover = dark
    ? "hover:text-lime-50 focus-visible:text-lime-50"
    : "hover:text-lime-90 focus-visible:text-lime-90"
  const pillColor = dark ? "bg-white/10" : "bg-accent"
  const borderColor = dark ? "border-white/10" : "border-border"
  // The system lime ring. --ring (lime-70 at half alpha) sits under 3:1 on
  // the Dark Lime ground (see button.tsx), so dark surfaces ring in Lime-50.
  const navFocus = dark
    ? "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-50/70"
    : "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"

  // Frosted chrome once content scrolls under the sticky header. Over light
  // sections it's the translucent paper wash; over a dark section the same
  // frost re-tints to the Dark Lime surface itself, so the band reads as a
  // seamless blur of the green rather than a cream wash across it. The
  // higher-opacity variant is the fallback for browsers without
  // backdrop-filter, where the tint alone must separate header from content.
  const raised = scrolled || open
  const frost = dark
    ? "border-white/10 bg-lime-90/85 supports-[backdrop-filter]:bg-lime-90/60 shadow-[0_1px_0_0_rgba(255,255,255,0.04),0_8px_24px_-12px_rgba(0,0,0,0.45)]"
    : "border-border bg-background/75 supports-[backdrop-filter]:bg-background/60 shadow-[0_1px_0_0_rgba(0,0,0,0.02),0_8px_24px_-12px_rgba(0,0,0,0.12)]"

  useEffect(() => {
    if (themeDark) return
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [themeDark])

  // Watch the dark surfaces; the observed band spans the header's own height
  // at the top of the viewport, so the ink flips the moment a dark surface
  // slides under the chrome — not only once it reaches the viewport top,
  // which would wash light frost over the panel for the header's full
  // height of scroll. Full-bleed dark sections are found by class; dark
  // panels inside light sections (the featured case panel, the retainer
  // band) opt in with `data-header-dark` — an explicit marker, not a bare
  // `.dark` query, so a small dark chip can never flip the header. Only
  // relevant for the sticky (light-theme) header; the static dark-theme
  // header never auto-flips.
  useEffect(() => {
    if (themeDark) return
    const darkSections = Array.from(
      document.querySelectorAll<HTMLElement>(
        "section.dark, footer.dark, [data-header-dark]",
      ),
    )
    if (darkSections.length === 0) return

    const behind = new Map<Element, boolean>()
    let observer: IntersectionObserver | null = null

    // rootMargin can't express "header height" directly, so the band is
    // rebuilt from measured pixels on resize.
    const connect = () => {
      observer?.disconnect()
      behind.clear()
      const band = headerRef.current?.offsetHeight ?? 0
      observer = new IntersectionObserver(
        (entries) => {
          for (const e of entries) behind.set(e.target, e.isIntersecting)
          setOnDark(Array.from(behind.values()).some(Boolean))
        },
        {
          // 1px short of the header's own height: a dark section whose top
          // edge exactly abuts the header's bottom (a dark hero band under
          // the in-flow header) must not count as "under" it — threshold 0
          // treats edge-touching as intersecting.
          rootMargin: `0px 0px ${band - window.innerHeight - 1}px 0px`,
          threshold: 0,
        },
      )
      darkSections.forEach((s) => observer?.observe(s))
    }

    connect()
    window.addEventListener("resize", connect)
    return () => {
      window.removeEventListener("resize", connect)
      observer?.disconnect()
    }
  }, [themeDark])

  return (
    <header
      ref={headerRef}
      className={
        themeDark
          ? `relative z-10 border-b ${borderColor}`
          : `sticky top-0 z-50 border-b transition-[background-color,border-color,box-shadow,backdrop-filter] duration-300 ease-out ${
              raised ? `backdrop-blur-md ${frost}` : "border-transparent"
            }`
      }
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 font-sans md:px-8">
        <Link
          href="/"
          className="flex items-center"
          aria-label="Go Between home"
        >
          {/* Phone widths get the favicon treatment — the mark on a Dark
              Lime tile — because the full lockup next to the contact button
              and menu toggle crowds a 375px header. The tile also carries the
              mark-snap entrance, once per session (see SiteLogoMark). The
              lockup's width/height are its intrinsic viewBox ratio so the
              browser reserves the right box before the SVG loads — no header
              reflow; the h-10 w-auto classes set the rendered size. */}
          <SiteLogoMark className="sm:hidden" />
          <img
            src={
              dark
                ? "/logos/go-between-logo-on-dark.svg"
                : "/logos/go-between-logo-on-light.svg"
            }
            alt="Go Between"
            width={2000}
            height={436}
            className="hidden h-10 w-auto sm:block"
          />
        </Link>

        <NavigationMenu
          viewport={false}
          className="ml-auto hidden pr-10 md:flex"
          onMouseLeave={() => setHovered(null)}
        >
          <NavigationMenuList className={`text-meta gap-1 ${navText}`}>
            {navItems.map((item) => (
              <NavigationMenuItem key={item.href}>
                <Link
                  href={item.href}
                  aria-current={isActive(item.href) ? "page" : undefined}
                  onMouseEnter={() => setHovered(item.href)}
                  onFocus={() => setHovered(item.href)}
                  onBlur={() => setHovered(null)}
                  className={`relative rounded-full px-4 py-2 whitespace-nowrap transition-colors ${
                    isActive(item.href) ? navActive : ""
                  } ${navHover} ${navFocus}`}
                >
                  {pillAt === item.href && (
                    <m.span
                      layoutId="nav-pill"
                      className={`absolute inset-0 -z-10 rounded-full ${pillColor}`}
                      transition={
                        reduceMotion ? { duration: 0 } : springInteractive
                      }
                    />
                  )}
                  <span className="relative">{item.label}</span>
                </Link>
              </NavigationMenuItem>
            ))}
          </NavigationMenuList>
        </NavigationMenu>

        <div className="flex items-center gap-2">
          {/* Below md this is the page's only persistent booking path, so it
              meets the 44px touch floor there (matching the size-11 toggle
              beside it — the header band doesn't grow). On /contact it jumps
              to the booking calendar instead of navigating: an Inertia visit
              to the current page would remount it and wipe a half-typed
              inquiry message. */}
          <Button
            variant={dark ? "paper" : "ink"}
            size="sm"
            className="max-md:h-11 max-md:px-4"
            asChild
          >
            {path === "/contact" ? (
              <a href="#book-a-call">Book a call</a>
            ) : (
              <Link href="/contact">Book a call</Link>
            )}
          </Button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="site-nav-panel"
            aria-label={open ? "Close menu" : "Open menu"}
            className={`inline-flex size-11 items-center justify-center rounded-md focus:outline-none md:hidden ${navText} ${navHover} ${navFocus}`}
          >
            <ToggleIcon open={open} />
          </button>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <m.div
            id="site-nav-panel"
            key="panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={
              reduceMotion
                ? { duration: 0 }
                : { ...springInteractive, opacity: { duration: 0.2 } }
            }
            className={`overflow-hidden border-t md:hidden ${borderColor}`}
          >
            <nav
              className={`mx-auto max-w-6xl border-x px-4 py-6 ${borderColor}`}
            >
              <ul className="flex flex-col gap-1 text-lg">
                {navItems.map((item) => (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      aria-current={isActive(item.href) ? "page" : undefined}
                      onClick={() => setOpen(false)}
                      className={`-mx-3 block rounded-md px-3 py-2 ${
                        isActive(item.href)
                          ? `${navActive} ${pillColor}`
                          : navText
                      } ${navHover} ${navFocus}`}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </m.div>
        )}
      </AnimatePresence>
    </header>
  )
}
